import ReCAPTCHA from 'react-google-recaptcha'
import { Form } from 'react-final-form'
import I18n from 'translations/i18n'
import { TextInputField } from 'components/final-form'
import { Button, Spacer, Typography } from '@jisr-hr/ds'
import classNames from 'classnames'
import { labels } from 'utils/whitelabel'
import { required } from 'components/global/form/FormValidations'
import { History } from 'history'
import { useMemo } from 'react'

export type TValuesLogin = {
  login?: string
  password?: string
}

type Props = {
  fetching: boolean
  showCaptcha: boolean
  onCaptchaChange: (val: string | null) => void
  history: History
  onSubmit: (vals: TValuesLogin) => void
}

const LoginForm = ({
  fetching,
  history,
  showCaptcha,
  onCaptchaChange,
  ...props
}: Props): JSX.Element => {
  const domainSplit = window.location.hostname.split('.')
  const domain = domainSplit.splice(1, domainSplit.length).join('.')

  const login = useMemo(
    () =>
      document.cookie
        .split('; ')
        .find((row) => row.startsWith('email='))
        ?.split('=')[1] || '',
    [],
  )
  const otp_code = useMemo(
    () =>
      document.cookie
        .split('; ')
        .find((row) => row.startsWith('otp_code='))
        ?.split('=')[1] || '',
    [fetching],
  )

  if (login) {
    document.cookie = `email=; path=/; domain=.${domain}; ${document.cookie}`
  }

  if (otp_code) {
    document.cookie = `otp_code=; path=/; domain=.${domain}; ${document.cookie}`
  }

  const initialValues = useMemo(
    () => ({ login, otp_code, ...(!!otp_code && { is_trusted_login: true }) }),
    [otp_code],
  )

  return (
    <Form
      onSubmit={props.onSubmit}
      validate={(vals): TValuesLogin => ({
        login: required(vals.login),
        password: required(vals.password),
      })}
      initialValues={initialValues}
    >
      {({ handleSubmit, submitting }): JSX.Element => (
        <form
          onSubmit={handleSubmit}
          className={classNames('login-form', labels.name === 'Maken' && 'maken-form')}
        >
          {/* @ts-expect-error Need to migrate TextInputField.js to TypeScript */}
          <TextInputField
            testId="login-username"
            name="login"
            type="text"
            label={I18n.t('email')}
            style={{ width: '100%' }}
          />
          <Typography
            text={I18n.t('forgot_password')}
            className="forget-link"
            variant="caption"
            onClick={(): void => history.push('/forgot_password')}
            data-testid="forgot-password"
          />
          {/* @ts-expect-error Need to migrate TextInputField.js to TypeScript */}
          <TextInputField
            testId="login-password"
            name="password"
            type="password"
            label={I18n.t('password')}
            style={{ width: '100%' }}
          />
          {showCaptcha && import.meta.env.REACT_APP_RECAPTCHA_KEY && (
            <ReCAPTCHA
              style={{ display: 'flex', justifyContent: 'center', marginTop: 10 }}
              sitekey={import.meta.env.REACT_APP_RECAPTCHA_KEY}
              onChange={onCaptchaChange}
            />
          )}
          <Spacer height="8px" />
          <Button
            data-testid="login-button"
            disabled={fetching || submitting}
            variant="primary"
            label={I18n.t('login')}
            type="submit"
            className={classNames(
              'login-btn-full',
              labels.name === 'Maken' && 'maken-btn',
              labels.name === 'Ahad' && 'ahad-btn',
            )}
            onClick={handleSubmit}
          />
        </form>
      )}
    </Form>
  )
}

export default LoginForm
